import React, { PureComponent } from 'react';
import { Link, StaticQuery, graphql } from 'gatsby';
import Img from 'gatsby-image';
import { css } from 'glamor';
import { Button, Glyphicon } from 'react-bootstrap';

import { grayBorder } from '../utilities/styles';

const navbarMenuLinkClass = css({
    padding: '10px 25px',
    color: '#0099CC',
    fontSize: 16,
    textDecoration: 'none !important',
    display: 'block',
    transition: '0.2s all ease',
    ':hover': {
        color: '#858686',
    }
});

const navbarMenuItemClass = css({
    '& + &': {
        borderTop: `1px solid ${grayBorder}`,
    }
});

const navbarMenuItemClassMobile = css({
    height: 40,
    borderTop: `1px solid ${grayBorder}`,
});

const navbarMenuBaseClass = css({
    listStyle: 'none',
    border: `1px solid ${grayBorder}`,
    position: 'absolute',
    backgroundColor: 'white',
    padding: 0,
    margin: 0,
    width: 208,
    textAlign: 'left',
    top: 24,
    transition: '0.4s opacity ease, 0.4s top ease',
    opacity: 0,
    zIndex: 1,
    visibility: 'hidden',
});

const navbarMenuClassLeft = css(navbarMenuBaseClass, {
    left: 0,
});

const navbarMenuClassCenter = css(navbarMenuBaseClass, {
    left: '50%',
    transform: 'translateX(-50%)',
});

const navbarMenuClassRight = css(navbarMenuBaseClass, {
    right: 0,
});

const navbarMenuClassMobile = css({
    listStyle: 'none',
    padding: 0,
});

const navbarItemHeaderClass = css({
    color: '#3d3f41',
    fontSize: 16,
    textDecoration: 'none !important',
    fontWeight: 400,
    transition: '0.2s all ease',
    position: 'relative',
    whiteSpace: 'nowrap',
    ':before': {
        content: '""',
        height: 2,
        backgroundColor: '#0099CC',
        position: 'absolute',
        left: '50%',
        right: '50%',
        top: 20,
        opacity: 0,
        transition: '0.2s',
    }
});

const navbarItemHeaderClassMobile = css({
    color: '#3d3f41',
    display: 'block',
    height: 40,
    lineHeight: '40px',
    textDecoration: 'none !important',
    transition: '0.2s all ease',
    ':hover': {
         color: '#0099CC',
    }
});

const navbarItemClass = css({
    position: 'relative',
    marginLeft: 24,
    ':hover:before': {
        content: '""',
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        height: 40,
    },
    [`&:hover .${navbarMenuClassLeft}, &:hover .${navbarMenuClassCenter}, &:hover .${navbarMenuClassRight}`]: {
        top: 30,
        opacity: 1,
        visibility: 'visible',
    },
    [`:hover .${navbarItemHeaderClass}`]: {
        color: '#0099CC',
    },
    [`:hover .${navbarItemHeaderClass}:before`]: {
        left: 0,
        right: 0,
        opacity: 1,
    }
});

const navbarMenuClassMap = {
    left: navbarMenuClassLeft,
    center: navbarMenuClassCenter,
    right: navbarMenuClassRight,
};

const navbarItemClassMobile = css({
    fontSize: 16,
    width: '100%',
    borderTop: `1px solid ${grayBorder}`,
});

const NavbarItem = ({ headerText, menuItems, headerHref, alignment = 'left', mobile, toggleMobileNav }) => (
    <li className={mobile ? navbarItemClassMobile : navbarItemClass}>
        <Link className={mobile ? navbarItemHeaderClassMobile : navbarItemHeaderClass} to={headerHref} onClick={toggleMobileNav}>{headerText}</Link>
        {menuItems.length ? (<ul className={mobile ? navbarMenuClassMobile : navbarMenuClassMap[alignment]}>
            {menuItems.map(({ text, href }, index) => (
                <li className={mobile ? navbarMenuItemClassMobile : navbarMenuItemClass} key={index}>
                    <Link className={navbarMenuLinkClass} to={href} onClick={toggleMobileNav}>
                        {text}
                    </Link>
                </li>
            ))}
        </ul>) : null}
    </li>
);

const Navbar = ({ mobile, toggleMobileNav }) => (
    <ul
        css={{
            listStyle: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 0,
            padding: 0,
            [`@media(${mobile ? 'min' : 'max'}-width: 76${mobile ? '8' : '7'}px)`]: {
                display: 'none',
            },
            position: mobile && 'absolute',
            left: mobile && 0,
            right: mobile && 0,
            top: mobile && 54,
            flexDirection: mobile ? 'column' : 'row',
            backgroundColor: 'white',
            zIndex: 1,
            borderBottom: mobile && `1px solid ${grayBorder}`,
        }}
    >
        <NavbarItem
            mobile={mobile}
            toggleMobileNav={toggleMobileNav}
            headerText="Calculators"
            headerHref="/calculators"
            menuItems={[
                { text: 'Home Purchase Calculator', href: '/calculators/home' },
                { text: 'Car Purchase Calculator', href: '/calculators/car' },
                { text: 'Investment Calculator', href: '/calculators/investment' },
                { text: 'Terms & Knowledge', href: '/terms-and-knowledge' }
            ]}
        />
        <NavbarItem
            mobile={mobile}
            toggleMobileNav={toggleMobileNav}
            headerText="Homes"
            headerHref="/category/homes"
            menuItems={[
                { text: 'Home Purchase Calculator', href: '/calculators/home' },
                { text: 'Home Purchase Articles', href: '/articles/home' },
                { text: 'Home Purchase Wyse Awards', href: '/wyse-awards/home' },
                { text: 'Terms & Knowledge', href: '/terms-and-knowledge' }
            ]}
        />
        <NavbarItem
            mobile={mobile}
            toggleMobileNav={toggleMobileNav}
            headerText="Cars"
            headerHref="/category/cars"
            alignment="center"
            menuItems={[
                { text: 'Car Purchase Calculator', href: '/calculators/car' },
                { text: 'Car Purchase Articles', href: '/articles/car' },
                { text: 'Car Purchase Wyse Awards', href: '/wyse-awards/car' },
                { text: 'Terms & Knowledge', href: '/terms-and-knowledge' }
            ]}
        />
        <NavbarItem
            mobile={mobile}
            toggleMobileNav={toggleMobileNav}
            headerText="Investments"
            headerHref="/category/investments"
            alignment="center"
            menuItems={[
                { text: 'Investment Calculator', href: '/calculators/investment' },
                { text: 'Investment Articles', href: '/articles/investment' },
                { text: 'Investment Wyse Awards', href: '/wyse-awards/investment' },
                { text: 'Terms & Knowledge', href: '/terms-and-knowledge' }
            ]}
        />
        <NavbarItem
            mobile={mobile}
            toggleMobileNav={toggleMobileNav}
            headerText="Data"
            headerHref="/data"
            alignment="center"
            menuItems={[]}
        />
        <NavbarItem
            mobile={mobile}
            toggleMobileNav={toggleMobileNav}
            headerText="Articles"
            headerHref="/articles"
            alignment="right"
            menuItems={[
                { text: 'Home Purchase Articles', href: '/articles/home' },
                { text: 'Car Purchase Articles', href: '/articles/car' },
                { text: 'Investment Articles', href: '/articles/investment' },
                { text: 'Lifestyle Articles', href: '/articles/lifestyle' },
                { text: 'Terms & Knowledge', href: '/terms-and-knowledge' }
            ]}
        />
        <NavbarItem
            mobile={mobile}
            toggleMobileNav={toggleMobileNav}
            headerText="Awards"
            headerHref="/wyse-awards"
            alignment="right"
            menuItems={[
                { text: 'Home Purchase Wyse Awards', href: '/wyse-awards/home' },
                { text: 'Car Purchase Wyse Awards', href: '/wyse-awards/car' },
                { text: 'Investment Wyse Awards', href: '/wyse-awards/investment' },
                { text: 'Lifestyle Wyse Awards', href: '/wyse-awards/lifestyle' },
                { text: 'Travel Wyse Awards', href: '/wyse-awards/travel' },
                { text: 'Terms & Knowledge', href: '/terms-and-knowledge' }
            ]}
        />
    </ul>
);

const Logo = () => (
    <StaticQuery
        query={graphql`
            query LogoQuery {
                file(relativePath: { eq: "logo.jpg" }) {
                    childImageSharp {
                        fixed(width: 196, height: 46, quality: 100) {
                           ...GatsbyImageSharpFixed_noBase64
                        }
                    }
                }
            }
        `}
        render={({ file: { childImageSharp: { fixed } } }) => (
            <img
                alt="Walletwyse"
                css={{
                    height: 'auto',
                    width: 196,
                    marginLeft: -13
                }}
                {...fixed}
            />
        )}
    />
);

export default class Header extends PureComponent {
    state = {
        mobileNavOpen: false,
    }

    toggleMobileNav = () => {
        this.setState(prevState => ({ mobileNavOpen: !prevState.mobileNavOpen }), this.props.onToggleMobileNav);
    }

    render() {
        return (
            <header
                css={{
                    width: '100%',
                    height: 54,
                    backgroundColor: 'white',
                }}
            >
                <div
                    css={{
                        maxWidth: 1024,
                        padding: '0 24px',
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        height: '100%',
                        margin: '0 auto',
                    }}
                >
                    <Link to="/" css={{ width: 172, overflow: 'hidden' }}>
                        <Logo />
                    </Link>
                    <span
                        css={{
                            '@media(min-width: 768px)': {
                                display: 'none'
                            },
                            '& .glyphicon-menu-hamburger': {
                                color: '#333',
                                fontSize: 20,
                                verticalAlign: 'middle',
                            },
                            '&&& .btn': {
                                padding: 12,
                                outline: 'none',
                            }
                        }}
                    >
                        <Button bsStyle="link" onClick={this.toggleMobileNav}>
                            <Glyphicon glyph="menu-hamburger" />
                        </Button>
                    </span>
                    <Navbar />
                    {this.state.mobileNavOpen && <Navbar mobile toggleMobileNav={this.toggleMobileNav} />}
                </div>
            </header>
        );
    }
}
